/**
 * BarChart Component
 *
 * Recharts BarChart wrapper with Safaqat 24 styling.
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { formatAmount } from '../../utils/numberFormat';

export interface BarChartData {
  name: string;
  value: number;
  color?: string;
}

export interface BarConfig {
  dataKey: string;
  name: string;
  color: string;
  radius?: number;
}

export interface BarChartProps {
  data: BarChartData[];
  bars?: BarConfig[];
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  layout?: 'horizontal' | 'vertical';
  xAxisDataKey?: string;
  colorByData?: boolean;
  className?: string;
}

export default function BarChart({
  data,
  bars,
  height = 300,
  showGrid = true,
  showLegend = false,
  layout = 'horizontal',
  xAxisDataKey = 'name',
  colorByData = false,
  className = '',
}: BarChartProps) {
  const defaultColor = 'var(--pv-color-primary)';
  const defaultBars: BarConfig[] = bars || [
    { dataKey: 'value', name: 'Value', color: defaultColor, radius: 4 },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-[var(--pv-color-surface-strong)] p-3 rounded-xl shadow-lg border border-[var(--pv-border-soft)]">
          <p className="text-sm font-medium text-[var(--pv-color-text)] mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              className="text-sm"
              style={{ color: entry.color }}
            >
              {entry.name}: {formatAmount(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={className} style={{ direction: 'ltr' }}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          layout={layout}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--pv-border-soft)"
              vertical={layout === 'horizontal'}
              horizontal={layout === 'vertical'}
            />
          )}
          {layout === 'horizontal' ? (
            <>
              <XAxis
                dataKey={xAxisDataKey}
                tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
                axisLine={{ stroke: 'var(--pv-border-soft)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatAmount(value)}
              />
            </>
          ) : (
            <>
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
                axisLine={{ stroke: 'var(--pv-border-soft)' }}
                tickLine={false}
                tickFormatter={(value) => formatAmount(value)}
              />
              <YAxis
                dataKey={xAxisDataKey}
                type="category"
                tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                width={100}
              />
            </>
          )}
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: 20 }}
              formatter={(value) => (
                <span className="text-sm text-[var(--pv-color-text)]">{value}</span>
              )}
            />
          )}
          {defaultBars.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name}
              fill={bar.color}
              radius={bar.radius || 0}
            >
              {colorByData &&
                data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || bar.color} />
                ))}
            </Bar>
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
